import * as React from "react";
import { Link, RouteComponentProps } from "react-router-dom";

import { Page, PageInner, PageSection } from "@app/shared";
import { QESButton, WizardNavigationBar, WizardSteps } from "@app/shared/qes-standards";
import { DownloadServiceAreaButton } from "@app/region-sets/new-editor/DownloadServiceAreaButton";
import { focusFirstInteractiveElement } from "@app/shared/utility";
import { NewActionMenu, NewActionMenuItem } from "@app/shared/action-menu";

type WizardStepProps = {
  actionButton?: React.ReactNode | (() => React.ReactNode);
  baseUrl: string;
  children?: React.ReactNode;
  heading?: string;
  nextUrl?: string;
  prevUrl?: string;
  progressList: string[];
  submitUrl?: string;
  subheading?: string;
  isNextDisabled?: boolean;
  isPrevDisabled?: boolean;
  onPrevClick?: () => void;
  onNextClick?: () => void;
  onSubmitClick?: (runNow: boolean) => void;
  onWizardStepsNavigation?: () => void;
  submitted?: boolean;
} & RouteComponentProps;

class WizardStep extends React.Component<WizardStepProps> {
  private stepBodyRef = React.createRef<HTMLDivElement>();

  private readonly submitOptions = [
    {
      key: "true",
      name: "Run Now",
    },
    {
      key: "false",
      name: "Run Later",
    },
  ];
  constructor(props: WizardStepProps) {
    super(props);
  }

  public componentDidMount() {
    focusFirstInteractiveElement(this.stepBodyRef);
  }

  public handlePrev = (): void => {
    if (this.props.onPrevClick) {
      this.props.onPrevClick();
    }

    this.props.history.push(`${this.props.baseUrl}` + "/" + `${this.props.prevUrl}`);
  }

  public handleNext = (): void => {
    if (this.props.onNextClick) {
      this.props.onNextClick();
    }
    this.props.history.push(`${this.props.baseUrl}` + "/" + `${this.props.nextUrl}`);
  }

  public handleStepChange = (newStep: number): void => {
    if (this.props.onWizardStepsNavigation) {
      this.props.onWizardStepsNavigation();
    }
    // newStep will be 0-based but the page numbers are 1-based, so add 1
    this.props.history.push(`${this.props.baseUrl}` + "/" + `${newStep + 1}`);
  }

  public handleFormSubmit = async (runNow: boolean): Promise<void> => {
    if (this.props.onSubmitClick) {
      this.props.onSubmitClick(runNow);
    }
    // TODO setup where to go after submit
    // this.props.history.push(`${this.props.baseUrl}` + "/" + `${this.props.submitUrl}`);
  }

  public render() {
    const renderPrev = (this.props.prevUrl || this.props.onPrevClick) ? <QESButton qesTestId="wizard-step_previous" qesType="secondary" disabled={this.props.isPrevDisabled} onClick={this.handlePrev}>Previous</QESButton> : "";
    const renderNext = (this.props.nextUrl || this.props.onNextClick)
      ? <QESButton qesTestId="wizard-step_next" qesType="primary" onClick={this.handleNext} disabled={this.props.isNextDisabled}>Next</QESButton>
      : (
        <div className="pull-right">
          <NewActionMenu
            alignRight={true}
            buttonText="Submit"
            testId="wizard-step__submit-dropdown"
            buttonClassNames="qes-button-primary"
            dropup={true}
            loading={this.props.isNextDisabled}
          >
            {this.submitOptions.map((option) => {
              return (
                <NewActionMenuItem
                  display={option.name}
                  isSelected={false}
                  key={option.key}
                  onClick={() => this.handleFormSubmit(option.key === "true")}
                  testId={`wizard-step__submit-option--${option.key}`}
                />
              );
            })}
          </NewActionMenu>
        </div>
      );
    const renderActionButton = this.props.actionButton
      ? typeof (this.props.actionButton) === "function"
        ? (this.props.actionButton as () => React.ReactNode)()
        : this.props.actionButton
      : null;

    // +1 because 0-indexed array vs 1-indexed url
    const serviceAreaPageNumber = this.props.progressList.findIndex((i) => i === "Service Area") + 1;
    const downloadServiceAreaButton = parseInt(this.props.location.pathname.split("/").pop(), 10) === serviceAreaPageNumber ?
      (<div className="ml-4 d-inline-block">
        <DownloadServiceAreaButton />
      </div>) :
      null;

    return (
      <Page documentTitle="Create New Project">
        <PageInner>
          <header className="page-title-bar row align-items-center mt-4 mb-3">
            <h1 className="page-title mt-0 mb-0">{this.props.heading}</h1>
          </header>
          <WizardSteps currentStep={this.getCurrentStep()} steps={this.props.progressList} setStep={this.handleStepChange} />
          <hr />
          <PageSection>
            <div className="row">
              <div className="col-12">
                <p className="lead">
                  {this.props.subheading}
                </p>
              </div>
            </div>
            <div className="row" ref={this.stepBodyRef}>
              <div className="col-12 form-horizontal">
                {this.props.children}
              </div>
            </div>
          </PageSection>
        </PageInner>
        <WizardNavigationBar
          leftSide={(
            <div className="col-6">
              <div className="col-6 pl-0 d-inline-block">
                <Link className="qes-button qes-button-secondary danger" type="button" to="/" >Cancel</Link>
              </div>
              {downloadServiceAreaButton}
            </div>
          )}
          rightSide={(
            <div>
              {renderActionButton}
              {renderPrev}
              {renderNext}
            </div>
          )}
        />
      </Page>
    );
  }

  private getCurrentStep() {
    /* The prevUrl/page url is 1-based but the wizard steps are 0-based.
     * That means we can use the prevUrl to signify the current page.
     */
    return (this.props.prevUrl ? +this.props.prevUrl : 0);
  }
}

export default WizardStep;
